import React, { FC, useCallback, useEffect, useState } from "react";
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from "react-redux";

import { existsAndNotEmpty } from "stentor-utils";
import { Button, LoadingSpinner, Onboarding, OnboardingStep, PageAlert } from "@xapp/components";

import { Credentials, State } from "redux/state";
import { useGetUserProfileQuery } from "generated/graphql";
import { getProducts, Product, supportsIntegrations } from "utils/getProducts";

import { CreateProps } from "../models";

import { AddressStepContainer } from "./AddressStepContainer";
import { ConfigureAndCreateStepContainer } from "./ConfigureAndCreateStepContainer";
import { SimpleConfigureAndCreateStepContainer } from "./SimpleConfigureAndCreateStepContainer";
import { CheckoutStepContainer } from "./CheckoutStepContainer";
import { IntegrateStepContainer } from "./IntegrateStepContainer";

import style from "./style.scss";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

function useAddQueryParams() {
    const history = useHistory();

    type QueryParamsType = { [key: string]: string };
    return useCallback((newParams: QueryParamsType) => {
        const searchParams = new URLSearchParams(history.location.search);
        for (const key in newParams) {
            const value = newParams[key];
            if (value && value !== "null") {
                searchParams.set(key, newParams[key]);
            }
        }
        history.replace({ ...history.location, search: searchParams.toString() });
    }, [history]);
}

export const OnboardingContainer: FC = () => {

    const history = useHistory();

    const setQueryParams = useAddQueryParams();
    const queryParams = useQueryParams();

    const email = queryParams.get("email");
    const website = queryParams.get("website");

    const appId = queryParams.get("appId");
    const organizationId = queryParams.get("organizationId");
    const executionId = queryParams.get("executionId");

    const cms = queryParams.get("cms");
    const partner = queryParams.get("partner");
    let product = queryParams.get("product");

    if (product === "starter" || !product) {
        product = "prod_QWRVWvZtQH6dBI"
    }

    let currentStep = queryParams.get("step");

    if (!currentStep && appId) {
        currentStep = "address"
    }

    const coupon = queryParams.get("coupon");

    const beta = queryParams.get("beta");
    const legacy = queryParams.get("legacy");
    const debug = queryParams.get("debug");

    const productName = getProducts({ partner }).find((p) => p.id === product)?.name;

    const [redirectUrl, setRedirectUrl] = useState<string>();

    const credentials = useSelector<State, Credentials>(state => state.auth.credentials);

    const [getProfile] = useGetUserProfileQuery({
        requestPolicy: "cache-and-network",
        variables: {
            jwt: credentials.accessToken
        }
    });

    // These keep track of the props that will create the app as we move steps
    const [appProps, setAppProps] = useState<CreateProps>(() => {
        // in case the user refreshes, we store them (down below) on session storage
        // and try to retreive them
        let storageProps: CreateProps;
        try {
            storageProps = JSON.parse(sessionStorage.getItem("onboarding-appProps"));
        } catch (e) {
            // don't care about the error
        }
        return storageProps || {};
    });

    // Synchronize the query string with the current step
    useEffect(() => {
        const searchParams = new URLSearchParams(history.location.search);
        const queryStep = searchParams.get("step");
        if (currentStep !== queryStep) {
            if (currentStep) {
                searchParams.set("step", currentStep);
            } else {
                searchParams.delete("step");
            }

            if (history.location.search !== searchParams.toString()) {
                history.replace({ ...history.location, search: searchParams.toString() });
            }
        }
    }, [currentStep, history]);

    // DEBUG
    const clearAppProps = useCallback(() => {
        setAppProps({});
        sessionStorage.removeItem("onboarding-appProps");
    }, []);

    /** Transition back to "welcome" */
    /*
    const handleStepBack = useCallback((step: string) => {
        setQueryParams({
            website,
            step,
            product,
            coupon,
            partner,
            email,
            cms,
            beta,
            executionId,
            legacy,
            debug
        });
    }, [beta, cms, coupon, debug, email, executionId, legacy, partner, product, setQueryParams, website]); */

    /** Transition to step "start-trial"  */
    const handleInitialAppCreated = useCallback(async (createProps: CreateProps) => {

        setAppProps((prevAppProps) => {

            const newAppProps: CreateProps = {
                ...prevAppProps,
                ...createProps
            };

            // save them to local storage too
            sessionStorage.setItem("onboarding-appProps", JSON.stringify(newAppProps));

            return newAppProps;
        });

        // clear these for next time
        // sessionStorage.removeItem("onboarding-appProps");
        setQueryParams({
            appId: createProps.appId,
            organizationId: createProps.organizationId,
            website: createProps.website,
            //  step: "start-trial",
            step: "address",
            product,
            coupon,
            partner,
            email,
            cms,
            beta,
            legacy,
            executionId: createProps.executionId,
            debug
        });
    }, [beta, cms, coupon, debug, email, legacy, partner, product, setQueryParams]);

    /** Transition to "address" */
    const handleAddressInput = useCallback(async (createProps: CreateProps) => {

        setAppProps((prevAppProps) => {

            const newAppProps: Partial<CreateProps> = {
                ...prevAppProps,
                ...createProps
            };

            // TODO: is it possible to get a website from place if they didn't provide us with one?
            // do we then start an analysis

            // save them to local storage too
            sessionStorage.setItem("onboarding-appProps", JSON.stringify(newAppProps));

            return newAppProps;
        });

        // Add the place
        setQueryParams({
            appId,
            organizationId,
            website,
            step: "integrate",
            product,
            coupon,
            partner,
            email,
            cms,
            beta,
            legacy,
            executionId,
            debug
        });
    }, [appId, beta, cms, coupon, debug, email, executionId, legacy, organizationId, partner, product, setQueryParams, website]);

    /** LEGACY Transition to step "integrate"  */
    /*
    const handleAppCreated = useCallback((createdApp: Pick<ClientApp, "appId" | "organizationId">) => {
        // clear these for next time
        sessionStorage.removeItem("onboarding-appProps");

        setQueryParams({
            appId: createdApp.appId,
            organizationId: createdApp.organizationId,
            website,
            step: "integrate",
            product,
            coupon,
            partner,
            email,
            cms,
            beta,
            legacy,
            executionId
        });
    }, [beta, cms, coupon, email, executionId, legacy, partner, product, setQueryParams, website]); */

    const handleUpgrade = useCallback(() => {
        const searchParams = new URLSearchParams(history.location.search);

        let leadsProduct: Product;

        const products = getProducts({ partner });

        // find leads+
        if (partner) {
            leadsProduct = products.find((product) => {
                return product.name.includes("Leads+");
            });
        } else {
            leadsProduct = products.find((product) => {
                return product.name === "XAPP AI Leads+";
            });
        }

        // this is when they request to upgrade to leads and we need track it for payment
        if (leadsProduct) {
            searchParams.set("product", leadsProduct.id);
        }

        if (history.location.search !== searchParams.toString()) {
            history.replace({ ...history.location, search: searchParams.toString() });
        }

    }, [history, partner]);

    /** Transition to step "checkout" */
    const handleIntegrateComplete = useCallback((createProps: CreateProps) => {

        // update app
        setAppProps((prevAppProps) => {

            const newAppProps: Partial<CreateProps> = {
                ...prevAppProps,
                ...createProps
            };

            // TODO: is it possible to get a website from place if they didn't provide us with one?
            // do we then start an analysis

            // save them to local storage too
            sessionStorage.setItem("onboarding-appProps", JSON.stringify(newAppProps));

            return newAppProps;
        });

        setQueryParams({
            appId,
            organizationId,
            website,
            step: "checkout",
            product,
            coupon,
            partner,
            email,
            cms,
            beta,
            legacy,
            executionId,
            debug
        })
    }, [setQueryParams, appId, organizationId, website, product, coupon, partner, email, cms, beta, legacy, executionId, debug]);

    const handleRedirectToHome = useCallback(() => {
        // Redirect them to app home

        let successPath = `/${organizationId}/${appId}/getting-started?checkout=success`;
        if (cms) {
            successPath += `&cms=${cms}`;
        }

        if (partner) {
            successPath += `&partner=${partner}`;
        }

        // go to the successPath
        setRedirectUrl(successPath);

    }, [appId, cms, organizationId, partner]);

    let welcomeMessage: string | undefined;

    switch (partner) {
        case "servicetitan":
            welcomeMessage = "Welcome ServiceTitan Customers!";
            break;
        case "lacrm":
            welcomeMessage = "Welcome Less Annoying CRM Customers!";
            break;
        case "jobber":
            welcomeMessage = "Welcome Jobber Customers!";
            break;
        case "sfl":
            welcomeMessage = "Welcome Surefire Local Customers!";
            break;
        case "housecallpro":
            welcomeMessage = "Welcome Housecall Pro Customers!";
            break;
        default:
            welcomeMessage = undefined;
    }

    // TODO: check to see if they have existing orgs and ask if they want to apply this to an existing app or create a new one.

    // copy the params
    const successPathParams = new URLSearchParams(history.location.search);
    successPathParams.set("step", "configure");

    const successPath = `/onboarding?${successPathParams.toString()}`;

    if (redirectUrl) {
        return <Redirect to={redirectUrl} />
    }

    const { data, error, fetching } = getProfile;


    if (fetching) {
        return (<LoadingSpinner loadingText={"Getting your profile..."} />);
    }

    if (error) {
        return (<PageAlert background={"orange"}><p>We had an error retreiving your profile. {error.message} You can try logging in again <a href="/login">here.</a></p></PageAlert>);
    }
    const profile = data.profile;

    return (
        <>
            <Onboarding>
                <OnboardingStep
                    title={"Welcome"}
                    active={!currentStep || currentStep === "welcome"}
                    done={currentStep === "address" || currentStep === "integrate" || currentStep === "checkout" || currentStep === "terms" || currentStep === "review"}
                >
                    <SimpleConfigureAndCreateStepContainer
                        profile={profile}
                        welcomeMessage={welcomeMessage}
                        initialProps={appProps}
                        product={product}
                        className={style.stepWrapper}
                        onNextStep={handleInitialAppCreated}
                        beta={beta === "true"}
                    />
                </OnboardingStep>
                <OnboardingStep
                    title="Address"
                    active={currentStep === "address"}
                    hideStep={true}
                    done={currentStep === "integrate" || currentStep === "checkout" || currentStep === "terms" || currentStep === "review"}
                >
                    <AddressStepContainer
                        app={appProps}
                        className={style.stepWrapper}
                        onNext={handleAddressInput}
                    />
                </OnboardingStep>
                <OnboardingStep
                    title={"Integrate"}
                    active={currentStep === "integrate"}
                    done={currentStep === "checkout" || currentStep === "terms" || currentStep === "review"}
                >
                    <IntegrateStepContainer
                        app={appProps}
                        /** Based on if they are starter or not */
                        integrationsSupported={supportsIntegrations(product)}
                        limitIntegrations={partner ? [partner] : undefined}
                        className={style.stepWrapper}
                        onUpgrade={handleUpgrade}
                        onNextStep={handleIntegrateComplete}
                    />
                </OnboardingStep>
                <OnboardingStep
                    title={"Start Trial"}
                    active={currentStep === "start-trial" || currentStep === "checkout" || currentStep === "terms"}
                    done={currentStep === "review"}
                >
                    <CheckoutStepContainer
                        successPath={successPath}
                        couponId={coupon}
                        productId={product}
                        cms={cms}
                        partner={partner}
                        app={appProps}
                        className={style.stepWrapper}
                    />
                </OnboardingStep>
                <OnboardingStep title={"Create"} active={currentStep === "configure" || currentStep === "review"}>
                    <ConfigureAndCreateStepContainer
                        initialProps={appProps}
                        executionId={executionId}
                        productId={product}
                        className={style.stepWrapper}
                        onNextStep={handleRedirectToHome}
                        beta={beta === "true"}
                    />
                </OnboardingStep>
            </Onboarding >
            {debug === "true" && (
                <div style={{ width: "100%", display: "flex", flexDirection: "column", alignContent: "center", alignItems: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flexWrap: "wrap" }}>
                        <b>Debug</b>
                        <p>Business: {appProps?.name}</p>
                        <p>Template: {appProps?.template?.name} {appProps?.template?.id}</p>
                        <p>Website: {appProps?.website}</p>
                        <p>Place: {appProps?.place?.formattedAddress}</p>
                        <p>Integration: {existsAndNotEmpty(appProps?.leadsIntegration) ? appProps.leadsIntegration[0] : ""}</p>
                        <p>Leads Email: {appProps?.leadsContact}</p>
                        <p>Product Name: {productName}</p>
                        <p>Product ID: {product}</p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "row", margin: "20px" }}>
                        <Button variation="secondary" label="Clear Props" onClick={clearAppProps} />
                        <Button label="Print Props" onClick={() => console.log(appProps)} />
                    </div>
                </div>
            )
            }
        </>
    );
};
